// Super-admin licensing — per-org detail.
//
// Fetches /api/admin/licensing/orgs/:id + /usage in parallel and renders:
//   • 3 SKU cards (one per known SKU; assigned vs not-assigned states)
//     — each assigned card exposes [Manage features (N)] which opens a
//       640px right-side drawer containing the FeatureTable (extracted
//       to components/pages/_new/super/licensing/FeatureTable.js on
//       2026-05-31; inline tables felt cluttered with ~35 rows stacked).
//   • Platform Features card — same drawer treatment, single button.
//   • Active breaches table (only if breaches present)
//   • Org limits section (max_sites, data_retention_days)
//
// Feature data comes from a separate per-SKU endpoint
// (GET /licensing/orgs/:org_id/skus/:sku_id/features) fetched on mount
// and refetched only on mutation or auto-refresh; the legacy
// "Feature overrides" SectionCard and M_OverrideFeature drawer were
// removed in Step 2.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Stack,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import TuneIcon from "@mui/icons-material/Tune";

import {
  TOKENS,
  PageHeader,
  SectionCard,
  StatusPill,
  RowMenu,
} from "../../../components/pages/_new/super/design";
import {
  getOrg,
  getOrgUsage,
  patchOrgLimits,
  getOrgSkuFeatures,
} from "../../../services/licensingService";
import adminAxios from "../../../utils/axios/v1/adminAxios";

import M_GrantSku from "../../../components/pages/_new/super/licensing/M_GrantSku";
import M_EditSku from "../../../components/pages/_new/super/licensing/M_EditSku";
import M_RevokeSku from "../../../components/pages/_new/super/licensing/M_RevokeSku";
import M_ResolveBreach from "../../../components/pages/_new/super/licensing/M_ResolveBreach";
import FeaturesDrawer from "../../../components/pages/_new/super/licensing/FeaturesDrawer";
import { PLATFORM_SKU_KEY } from "../../../components/pages/_new/super/licensing/FeatureTable";

const REFRESH_MS = 30_000;

const thSx = {
  padding: "10px 14px",
  borderBottom: `1px solid ${TOKENS.border}`,
  background: TOKENS.bgSunken,
  color: TOKENS.fg2,
  fontSize: 11.5,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};
const tdSx = {
  padding: "12px 14px",
  borderBottom: `1px solid ${TOKENS.borderSubtle}`,
  fontSize: 13,
  color: TOKENS.fg1,
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch (_) { return String(iso); }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); }
  catch (_) { return String(iso); }
};
const fmtBytes = (n) => {
  if (n == null) return "Unlimited";
  if (n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = Number(n);
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
};
const fmtInt = (n) => (n == null ? "Unlimited" : Number(n).toLocaleString());

const skuPill = (sku) => {
  if (sku.expired) {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          height: 24,
          px: "10px",
          borderRadius: 999,
          background: TOKENS.dangerBg,
          color: TOKENS.dangerInk,
          border: `1px solid ${TOKENS.dangerBorder}`,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: TOKENS.danger }} />
        Expired
      </Box>
    );
  }
  if (sku.is_trial) return <StatusPill status="Trial" />;
  return <StatusPill status="Active" />;
};

const daysLeftColor = (days) => {
  if (days == null) return TOKENS.fg2;
  if (days < 0) return TOKENS.dangerInk;
  if (days < 14) return TOKENS.dangerInk;
  if (days < 30) return TOKENS.warningInk;
  return TOKENS.fg2;
};

// "Manage features" button label. Parenthesised count surfaces SKU
// customisation at a glance without expanding the card. `undefined`
// count = features haven't loaded yet — show neutral "…" instead of "0".
const manageFeaturesLabel = (count) => {
  if (count == null) return "Manage features…";
  if (count === 0) return "Manage features";
  return `Manage features (${count})`;
};

const ManageFeaturesButton = ({ count, onClick, fullWidth = false, variant = "outlined" }) => {
  const isPrimary = variant === "primary";
  return (
    <Button
      onClick={onClick}
      startIcon={<TuneIcon style={{ width: 16, height: 16 }} />}
      fullWidth={fullWidth}
      sx={{
        textTransform: "none",
        fontWeight: 600,
        fontSize: 12.5,
        color: isPrimary ? "#fff" : TOKENS.fg1,
        background: isPrimary ? TOKENS.primary : "transparent",
        border: isPrimary ? "none" : `1px solid ${TOKENS.border}`,
        borderRadius: `${TOKENS.radiusXs}px`,
        px: 1.5,
        py: 0.5,
        "&:hover": {
          background: isPrimary ? TOKENS.primaryHover : TOKENS.bgSunken,
        },
      }}
    >
      {manageFeaturesLabel(count)}
    </Button>
  );
};

// ---------------- SKU card ----------------

const SkuCard = ({
  sku,            // shape from /orgs/:id.skus (when assigned) — undefined when not
  catalog,        // shape from /skus or available_skus
  usage,          // bySku[catalog.id] from /usage
  features,       // { rows, loading, error } for this SKU
  onGrant,
  onEdit,
  onRevoke,
  onManageFeatures,
}) => {
  const assigned = !!sku;
  const name = catalog?.name || sku?.sku_name || catalog?.id || sku?.sku_id;
  const description = catalog?.description || null;

  const overrideCount = assigned && features?.rows
    ? features.rows.filter((r) => r.is_overridden).length
    : undefined;

  return (
    <Box
      sx={{
        border: `1px solid ${TOKENS.border}`,
        borderRadius: `${TOKENS.radiusM}px`,
        background: TOKENS.bgSurface,
        boxShadow: TOKENS.shadowXs,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        sx={{
          p: "16px 18px",
          borderBottom: `1px solid ${TOKENS.borderSubtle}`,
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.fg1, letterSpacing: "-0.01em" }}>
            {name}
          </Typography>
          {description ? (
            <Typography sx={{ fontSize: 12.5, color: TOKENS.fg3, mt: 0.5 }}>{description}</Typography>
          ) : null}
        </Box>
        <Box>
          {assigned ? skuPill(sku) : <StatusPill status="Pending" />}
        </Box>
      </Box>

      <Box sx={{ p: "16px 18px", flex: 1 }}>
        {assigned ? (
          <Stack spacing={1.25}>
            <Row label="Expires">
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography sx={{ fontSize: 13, color: TOKENS.fg1, fontWeight: 600 }}>
                  {fmtDate(sku.expiry_date)}
                </Typography>
                {sku.days_until_expiry != null ? (
                  <Typography sx={{ fontSize: 12, color: daysLeftColor(sku.days_until_expiry) }}>
                    {sku.days_until_expiry >= 0
                      ? `(${sku.days_until_expiry} days left)`
                      : `(${Math.abs(sku.days_until_expiry)} days ago)`}
                  </Typography>
                ) : null}
              </Stack>
            </Row>
            <Row label="Request quota">
              <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>{fmtInt(sku.volume_limit_requests)}</Typography>
            </Row>
            <Row label="Byte quota">
              <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>{fmtBytes(sku.volume_limit_bytes)}</Typography>
            </Row>
            {usage ? (
              <>
                <Row label="Usage (30d, requests)">
                  <UsageBar
                    value={usage.requests}
                    limit={sku.volume_limit_requests}
                    fmt={fmtInt}
                    pct={usage.request_pct_of_limit}
                  />
                </Row>
                <Row label="Usage (30d, bytes)">
                  <UsageBar
                    value={usage.bytes}
                    limit={sku.volume_limit_bytes}
                    fmt={fmtBytes}
                    pct={usage.bytes_pct_of_limit}
                  />
                </Row>
              </>
            ) : null}
            <Row label="Granted by">
              <Typography sx={{ fontSize: 12.5, color: TOKENS.fg2 }}>
                {sku.granted_by_email || "(system)"} · {fmtDateTime(sku.granted_at)}
              </Typography>
            </Row>
            {sku.notes ? (
              <Row label="Notes">
                <Typography sx={{ fontSize: 12.5, color: TOKENS.fg2, whiteSpace: "pre-wrap" }}>
                  {sku.notes}
                </Typography>
              </Row>
            ) : null}
            {overrideCount != null && overrideCount > 0 ? (
              <Row label="Overrides">
                <Typography sx={{ fontSize: 12.5, color: TOKENS.primaryInk, fontWeight: 600 }}>
                  {overrideCount} feature{overrideCount === 1 ? "" : "s"} customised
                </Typography>
              </Row>
            ) : null}
          </Stack>
        ) : (
          <Stack spacing={1.5} sx={{ py: 1.5, alignItems: "flex-start" }}>
            <Typography sx={{ fontSize: 13, color: TOKENS.fg2 }}>
              Not yet assigned to this organisation.
            </Typography>
            <Button
              onClick={() => onGrant(catalog)}
              startIcon={<AddIcon style={{ width: 16, height: 16 }} />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                color: "#fff",
                background: TOKENS.primary,
                borderRadius: `${TOKENS.radiusXs}px`,
                px: 1.5,
                "&:hover": { background: TOKENS.primaryHover },
              }}
            >
              Grant SKU
            </Button>
          </Stack>
        )}
      </Box>

      {assigned ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            px: "12px",
            py: "10px",
            borderTop: `1px solid ${TOKENS.borderSubtle}`,
            background: TOKENS.bgSunken,
          }}
        >
          <ManageFeaturesButton
            count={overrideCount}
            onClick={() => onManageFeatures(sku.sku_id)}
          />
          <RowMenu
            actions={[
              { key: "edit", label: "Edit grant", onClick: () => onEdit(sku) },
              { key: "div", divider: true },
              { key: "revoke", label: "Revoke SKU", danger: true, onClick: () => onRevoke(sku) },
            ]}
          />
        </Box>
      ) : null}
    </Box>
  );
};

const Row = ({ label, children }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Typography
      sx={{
        fontSize: 11.5,
        color: TOKENS.fg3,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        fontWeight: 600,
        minWidth: 132,
        mt: 0.25,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Stack>
);

const UsageBar = ({ value, limit, fmt, pct }) => {
  const hasLimit = limit != null && limit > 0;
  const widthPct = hasLimit ? Math.min(100, Math.max(0, Number(pct) || 0)) : 0;
  const dangerous = widthPct >= 90;
  const warning = widthPct >= 75 && widthPct < 90;
  const barColor = dangerous ? TOKENS.danger : warning ? TOKENS.warning : TOKENS.primary;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography sx={{ fontSize: 12.5, color: TOKENS.fg1 }}>
          {fmt(value || 0)} {hasLimit ? `/ ${fmt(limit)}` : ""}
        </Typography>
        {hasLimit ? (
          <Typography sx={{ fontSize: 12, color: TOKENS.fg2, fontVariantNumeric: "tabular-nums" }}>
            {pct != null ? `${pct.toFixed(2)}%` : "—"}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, color: TOKENS.fg3 }}>No limit</Typography>
        )}
      </Stack>
      {hasLimit ? (
        <Box
          sx={{
            position: "relative",
            height: 6,
            borderRadius: 999,
            background: TOKENS.bgSunken,
            border: `1px solid ${TOKENS.borderSubtle}`,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              width: `${widthPct}%`,
              background: barColor,
              transition: "width 200ms ease",
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
};

// ---------------- Detail page ----------------

const LicensingDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { org_id: orgId } = useParams();

  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Per-SKU features view (catalog + current_value/source/is_overridden).
  // Shape: { [sku_id]: { rows, loading, error } }
  const [skuFeatures, setSkuFeatures] = useState({});
  const [snackbarMsg, setSnackbarMsg] = useState(null);

  // Drawer open-state: holds the sku_id ('web_security' / 'api_security' /
  // 'ai_security' / 'platform') of the currently-open Manage-features
  // drawer, or null when none is open. Single string keeps the
  // mutual-exclusion invariant for free — opening one drawer auto-closes
  // any other.
  const [drawerSku, setDrawerSku] = useState(null);

  // modal state — all share one shape for simplicity.
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantPresetSku, setGrantPresetSku] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [resolveBreachTarget, setResolveBreachTarget] = useState(null);

  // Refetch a single SKU's effective features. We tag every row with _orgId
  // so the embedded value cells can build mutation calls without prop
  // drilling orgId through three components.
  const fetchSkuFeatures = useCallback(async (skuId) => {
    if (!orgId || !skuId) return;
    setSkuFeatures((prev) => ({
      ...prev,
      [skuId]: { rows: prev[skuId]?.rows || [], loading: true, error: null },
    }));
    try {
      const res = await getOrgSkuFeatures(orgId, skuId);
      const raw = Array.isArray(res?.data) ? res.data : res?.data?.features || [];
      const rows = raw.map((r) => ({ ...r, _orgId: orgId }));
      setSkuFeatures((prev) => ({ ...prev, [skuId]: { rows, loading: false, error: null } }));
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to load features";
      setSkuFeatures((prev) => ({
        ...prev,
        [skuId]: { rows: prev[skuId]?.rows || [], loading: false, error: msg },
      }));
    }
  }, [orgId]);

  const fetchAll = useCallback(async () => {
    try {
      const [orgRes, usageRes] = await Promise.all([
        getOrg(orgId),
        getOrgUsage(orgId, { range: "30d" }).catch(() => ({ data: null })),
      ]);
      const orgData = orgRes?.data || null;
      setData(orgData);
      setUsage(usageRes?.data || null);
      setError(null);
      // Kick off per-SKU feature fetches for every assigned SKU; they fan
      // out independently into skuFeatures. Also fetch the platform-wide
      // features when the org has ≥1 active SKU (the only path that lets
      // an operator override them) — and ALSO when there are zero active
      // SKUs but the fetch surfaces stale overrides; the card renders
      // conditionally based on platform_rows + held SKUs (see card).
      const assignedSkuIds = (orgData?.skus || []).map((s) => s.sku_id);
      assignedSkuIds.forEach((id) => { fetchSkuFeatures(id); });
      fetchSkuFeatures(PLATFORM_SKU_KEY);
    } catch (e) {
      if (e?.response?.status === 404) {
        setError("Organisation not found.");
      } else {
        setError(e?.response?.data?.message || e?.message || "Failed to load");
      }
    } finally {
      setLoading(false);
    }
  }, [orgId, fetchSkuFeatures]);

  useEffect(() => {
    let cancelled = false;
    let iv = null;
    (async () => {
      await fetchAll();
      if (cancelled) return;
      if (autoRefresh) iv = setInterval(fetchAll, REFRESH_MS);
    })();
    return () => {
      cancelled = true;
      if (iv) clearInterval(iv);
    };
  }, [fetchAll, autoRefresh]);

  // Close the drawer on any route change so the user is never stuck
  // looking at the wrong org's features. react-router-dom's location
  // changes (incl. ?query updates) trigger this — orgId already comes
  // from URL params so the only realistic trigger is back/forward nav.
  useEffect(() => {
    setDrawerSku(null);
  }, [location.pathname]);

  // Open a Manage-features drawer for the given sku id. If features
  // for that SKU haven't been fetched yet (e.g. page just loaded and
  // user clicked fast), kick a fetch so the table populates while the
  // drawer slides in.
  const openManageFeatures = useCallback((skuId) => {
    setDrawerSku(skuId);
    if (skuId && !skuFeatures[skuId]) {
      fetchSkuFeatures(skuId);
    }
  }, [skuFeatures, fetchSkuFeatures]);

  const allKnownSkus = useMemo(() => {
    if (!data) return [];
    const assigned = data.skus || [];
    const available = data.available_skus || [];
    // Build the per-SKU card list using a stable order from the catalog:
    // available_skus carries `sort_order` for non-assigned, and assigned
    // rows don't — so we merge with a known precedence.
    const order = ["web_security", "api_security", "ai_security", "mcp_security"];
    return order.map((id) => {
      const sku = assigned.find((s) => s.sku_id === id);
      const cat = available.find((s) => s.id === id) ||
        (sku ? { id: sku.sku_id, name: sku.sku_name, description: null } : { id, name: id });
      return { id, sku, catalog: cat };
    });
  }, [data]);

  const platformRows = skuFeatures[PLATFORM_SKU_KEY]?.rows || [];
  const platformOverrideCount = platformRows.filter((r) => r.is_overridden).length;
  const hasActiveSku = (data?.skus || []).some((s) => !s.expired);
  const showPlatformCard = hasActiveSku || platformOverrideCount > 0;

  const headerActions = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <FormControlLabel
        sx={{ ml: 1, color: TOKENS.fg2, "& .MuiFormControlLabel-label": { fontSize: 12.5 } }}
        control={<Switch size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
        label="Auto-refresh"
      />
      <Button
        onClick={fetchAll}
        startIcon={<RefreshIcon style={{ width: 16, height: 16 }} />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: 12.5,
          color: TOKENS.fg1,
          border: `1px solid ${TOKENS.border}`,
          borderRadius: `${TOKENS.radiusXs}px`,
          px: 1.5,
          py: 0.5,
          "&:hover": { background: TOKENS.bgSunken },
        }}
      >
        Refresh
      </Button>
      <Button
        onClick={() => navigate("/super/licensing")}
        startIcon={<ArrowBackIcon style={{ width: 16, height: 16 }} />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: 12.5,
          color: TOKENS.fg1,
          border: `1px solid ${TOKENS.border}`,
          borderRadius: `${TOKENS.radiusXs}px`,
          px: 1.5,
          py: 0.5,
          "&:hover": { background: TOKENS.bgSunken },
        }}
      >
        Back to Licensing
      </Button>
    </Stack>
  );

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box>
        <Helmet title="Licensing — error" />
        <PageHeader title="Licensing" actions={headerActions} />
        <Box
          sx={{
            p: 2.5,
            borderRadius: `${TOKENS.radiusXs}px`,
            background: TOKENS.dangerBg,
            border: `1px solid ${TOKENS.dangerBorder}`,
            color: TOKENS.dangerInk,
            fontSize: 13,
          }}
        >
          {error}
        </Box>
      </Box>
    );
  }

  const orgTitle = data?.org?.title || "Organisation";
  const orgShortId = data?.org?.id ? data.org.id.slice(0, 8) : "—";

  return (
    <Box>
      <Helmet title={`${orgTitle} — Licensing`} />
      <PageHeader
        title={`${orgTitle} — Licensing`}
        subtitle={`Org ID: ${orgShortId}…`}
        actions={headerActions}
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {allKnownSkus.map(({ id, sku, catalog }) => (
          <Grid key={id} item xs={12} md={4}>
            <SkuCard
              sku={sku}
              catalog={catalog}
              usage={usage?.by_sku ? usage.by_sku[id] : null}
              features={sku ? skuFeatures[sku.sku_id] : null}
              onGrant={(cat) => { setGrantPresetSku(cat); setGrantOpen(true); }}
              onEdit={(s) => setEditTarget(s)}
              onRevoke={(s) => setRevokeTarget(s)}
              onManageFeatures={openManageFeatures}
            />
          </Grid>
        ))}
      </Grid>

      {/* Platform Features — features.sku_id IS NULL in the catalog
          (team_management, rbac, b2b_saml, cache, log_payload, alert
          numeric caps). Conditionally rendered: only when the org holds
          ≥1 active SKU (so overrides are actually enforceable) OR when
          there are stale platform overrides to clean up. */}
      {showPlatformCard ? (
        <PlatformFeaturesCard
          rowCount={platformRows.length}
          overrideCount={platformOverrideCount}
          hasActiveSku={hasActiveSku}
          onManageFeatures={() => openManageFeatures(PLATFORM_SKU_KEY)}
        />
      ) : null}

      {data?.active_breaches && data.active_breaches.length > 0 ? (
        <Box sx={{ mb: 2 }}>
          <SectionCard
            title="Active breaches"
            description={`${data.active_breaches.length} breach${data.active_breaches.length === 1 ? "" : "es"} not yet resolved`}
          >
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={thSx}>SKU</TableCell>
                    <TableCell sx={thSx}>Dimension</TableCell>
                    <TableCell sx={thSx}>Breach started</TableCell>
                    <TableCell sx={thSx}>Hard cap at</TableCell>
                    <TableCell sx={thSx} align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.active_breaches.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell sx={tdSx}>{b.sku_id}</TableCell>
                      <TableCell sx={tdSx}>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            px: "8px",
                            py: "2px",
                            borderRadius: 999,
                            background: TOKENS.warningBg,
                            color: TOKENS.warningInk,
                            border: `1px solid ${TOKENS.warningBorder}`,
                            fontSize: 11.5,
                            fontWeight: 600,
                          }}
                        >
                          {b.dimension}
                        </Box>
                      </TableCell>
                      <TableCell sx={tdSx}>{fmtDateTime(b.breach_started_at)}</TableCell>
                      <TableCell sx={tdSx}>{fmtDateTime(b.hard_cap_at)}</TableCell>
                      <TableCell sx={tdSx} align="right">
                        <RowMenu
                          actions={[
                            {
                              key: "resolve",
                              label: "Resolve",
                              onClick: () => setResolveBreachTarget(b),
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Box>
      ) : null}

      {/* Cert vendors — toggles + per-vendor cert quota. Backed directly
          by the existing super-admin SSL endpoints (no SKU projection
          yet — that ships as Option Y). See ssl-vendor-licensing-audit
          AUDIT.md for the design rationale. */}
      <CertVendorsCard orgId={orgId} onSaved={fetchAll} />

      {/* Org limits — replaces the legacy custom_packages.features[] path
          for max_sites + data_retention_days. NULL = unlimited. */}
      <OrgLimitsSection org={data?.org} orgId={orgId} onSaved={fetchAll} />

      {usage?.per_sku_attribution === "phase_3" ? (
        <Typography sx={{ fontSize: 11.5, color: TOKENS.fg3, mt: 1.5, fontStyle: "italic" }}>
          Usage attribution: per-SKU buckets are the org-wide totals in Phase 1b. Proper daily rollup lands in Phase 3.
          {usage?.ch_error ? ` · ClickHouse: ${usage.ch_error}` : ""}
        </Typography>
      ) : null}

      {/* Modals */}
      <M_GrantSku
        open={grantOpen}
        orgId={orgId}
        orgTitle={orgTitle}
        presetSku={grantPresetSku}
        availableSkus={data?.available_skus || []}
        onClose={() => { setGrantOpen(false); setGrantPresetSku(null); }}
        onSaved={() => { setGrantOpen(false); setGrantPresetSku(null); fetchAll(); }}
      />
      <M_EditSku
        open={!!editTarget}
        orgId={orgId}
        orgTitle={orgTitle}
        sku={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => { setEditTarget(null); fetchAll(); }}
      />
      <M_RevokeSku
        open={!!revokeTarget}
        orgId={orgId}
        orgTitle={orgTitle}
        sku={revokeTarget}
        activeBreachCount={(data?.active_breaches || []).filter((b) => b.sku_id === revokeTarget?.sku_id).length}
        onClose={() => setRevokeTarget(null)}
        onConfirmed={() => { setRevokeTarget(null); fetchAll(); }}
      />
      <M_ResolveBreach
        open={!!resolveBreachTarget}
        breach={resolveBreachTarget}
        orgTitle={orgTitle}
        onClose={() => setResolveBreachTarget(null)}
        onConfirmed={() => { setResolveBreachTarget(null); fetchAll(); }}
      />

      {/* Features drawers — one per known SKU + one for platform. We
          render them all up-front; only the one matching drawerSku
          actually opens. Keeping them mounted preserves the inline-edit
          state inside the FeatureTable across open/close cycles. */}
      {allKnownSkus.map(({ id, sku, catalog }) =>
        sku ? (
          <FeaturesDrawer
            key={`drawer-${id}`}
            open={drawerSku === sku.sku_id}
            onClose={() => setDrawerSku(null)}
            title={`${catalog?.name || sku.sku_name || id} — Features`}
            subtitle={catalog?.description || null}
            statusPill={skuPill(sku)}
            skuId={sku.sku_id}
            rows={skuFeatures[sku.sku_id]?.rows || []}
            loading={!!skuFeatures[sku.sku_id]?.loading}
            error={skuFeatures[sku.sku_id]?.error || null}
            onMutated={fetchSkuFeatures}
            onError={(msg) => setSnackbarMsg(msg)}
          />
        ) : null,
      )}
      <FeaturesDrawer
        open={drawerSku === PLATFORM_SKU_KEY}
        onClose={() => setDrawerSku(null)}
        title="Platform Features"
        subtitle="Cross-cutting features active when the org has any SKU"
        statusPill={
          hasActiveSku ? <StatusPill status="Active" /> : <StatusPill status="Inactive" />
        }
        skuId={PLATFORM_SKU_KEY}
        rows={platformRows}
        loading={!!skuFeatures[PLATFORM_SKU_KEY]?.loading}
        error={skuFeatures[PLATFORM_SKU_KEY]?.error || null}
        onMutated={fetchSkuFeatures}
        onError={(msg) => setSnackbarMsg(msg)}
      />

      <Snackbar
        open={!!snackbarMsg}
        autoHideDuration={6000}
        onClose={() => setSnackbarMsg(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarMsg(null)}
          severity="error"
          variant="filled"
          sx={{ fontSize: 13 }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// PlatformFeaturesCard — 4th card on the licensing detail page (after the
// 3 SKU cards). Lean version: shows only the org-wide pitch + an
// override count + a [Manage platform features (N)] button that opens
// the same FeaturesDrawer the SKU cards use.
//
// Differs from SkuCard in 3 ways (per spec):
//   1. No expiry/volume/granted-by metadata (platform features aren't
//      tied to a SKU expiry).
//   2. No "Edit grant" / "Revoke" actions (no SKU to edit).
//   3. Status pill: grey "Active" when ≥1 SKU held; "Inactive" when 0.
//
// Conditional render — only shows when the org has ≥1 active SKU OR
// when stale platform overrides exist (so an operator can clean up
// after a SKU was revoked); the gate lives in the parent.
// ---------------------------------------------------------------------------
function PlatformFeaturesCard({ rowCount, overrideCount, hasActiveSku, onManageFeatures }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          border: `1px solid ${TOKENS.border}`,
          borderRadius: `${TOKENS.radiusM}px`,
          background: TOKENS.bgSurface,
          boxShadow: TOKENS.shadowXs,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            p: "16px 18px",
            borderBottom: `1px solid ${TOKENS.borderSubtle}`,
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.fg1, letterSpacing: "-0.01em" }}>
              Platform Features
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: TOKENS.fg3, mt: 0.5 }}>
              Cross-cutting features active when the org has any SKU. Overrides apply org-wide, regardless of which SKU you stored them under.
            </Typography>
          </Box>
          <Box>
            {hasActiveSku ? (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  height: 24,
                  px: "10px",
                  borderRadius: 999,
                  background: TOKENS.bgSunken,
                  color: TOKENS.fg2,
                  border: `1px solid ${TOKENS.border}`,
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: TOKENS.fg3 }} />
                Active
              </Box>
            ) : (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  height: 24,
                  px: "10px",
                  borderRadius: 999,
                  background: TOKENS.dangerBg,
                  color: TOKENS.dangerInk,
                  border: `1px solid ${TOKENS.dangerBorder}`,
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: TOKENS.danger }} />
                Inactive (no active SKU)
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            px: "16px",
            py: "12px",
            background: TOKENS.bgSunken,
            borderTop: `1px solid ${TOKENS.borderSubtle}`,
          }}
        >
          <Typography sx={{ fontSize: 12.5, color: TOKENS.fg2 }}>
            {rowCount > 0
              ? `${rowCount} platform feature${rowCount === 1 ? "" : "s"} in catalog`
              : "Loading platform features…"}
            {overrideCount > 0 ? (
              <Box component="span" sx={{ color: TOKENS.primaryInk, fontWeight: 600, ml: 1 }}>
                · {overrideCount} override{overrideCount === 1 ? "" : "s"}
              </Box>
            ) : null}
          </Typography>
          <Button
            onClick={onManageFeatures}
            startIcon={<TuneIcon style={{ width: 16, height: 16 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.5,
              color: "#fff",
              background: TOKENS.primary,
              borderRadius: `${TOKENS.radiusXs}px`,
              px: 1.5,
              py: 0.5,
              "&:hover": { background: TOKENS.primaryHover },
            }}
          >
            Manage platform features{overrideCount > 0 ? ` (${overrideCount})` : ""}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// OrgLimitsSection — edits organisations.max_sites + .data_retention_days.
// Lives inline here (single caller). Inputs accept empty string to clear
// the cap (= unlimited).
// ---------------------------------------------------------------------------
function OrgLimitsSection({ org, orgId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [maxSitesStr, setMaxSitesStr] = useState("");
  const [retentionStr, setRetentionStr] = useState("");

  // Re-seed inputs when the underlying org payload changes.
  useEffect(() => {
    setMaxSitesStr(org?.max_sites == null ? "" : String(org.max_sites));
    setRetentionStr(org?.data_retention_days == null ? "" : String(org.data_retention_days));
  }, [org?.max_sites, org?.data_retention_days]);

  const parseField = (str) => {
    const trimmed = String(str || "").trim();
    if (trimmed === "") return null;
    const n = Number(trimmed);
    if (!Number.isInteger(n) || n < 0) return Symbol("bad");
    return n;
  };

  const save = async () => {
    setErr(null);
    const m = parseField(maxSitesStr);
    const r = parseField(retentionStr);
    if (typeof m === "symbol" || typeof r === "symbol") {
      setErr("Both fields must be non-negative integers (or blank for unlimited).");
      return;
    }
    setSaving(true);
    try {
      await patchOrgLimits(orgId, { max_sites: m, data_retention_days: r });
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const renderVal = (v) =>
    v == null
      ? <span style={{ color: TOKENS.fg3, fontStyle: "italic" }}>Unlimited</span>
      : <strong style={{ color: TOKENS.fg1 }}>{v}</strong>;

  return (
    <Box sx={{ mb: 2 }}>
      <SectionCard
        title="Org limits"
        description="Per-org numeric caps (replaces the legacy package.features[] path). Blank = unlimited."
        action={
          !editing ? (
            <Button size="small" variant="outlined" onClick={() => setEditing(true)}>Edit</Button>
          ) : (
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="text" disabled={saving} onClick={() => { setEditing(false); setErr(null); }}>Cancel</Button>
              <Button size="small" variant="contained" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</Button>
            </Stack>
          )
        }
      >
        {!editing ? (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, p: 2 }}>
            <Box>
              <Typography sx={{ fontSize: 11.5, color: TOKENS.fg3, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
                Max sites
              </Typography>
              <Typography sx={{ fontSize: 16 }}>{renderVal(org?.max_sites)}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 11.5, color: TOKENS.fg3, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
                Data retention (days)
              </Typography>
              <Typography sx={{ fontSize: 16 }}>{renderVal(org?.data_retention_days)}</Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, p: 2 }}>
            <Box>
              <Typography sx={{ fontSize: 11.5, color: TOKENS.fg3, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
                Max sites
              </Typography>
              <input
                type="text"
                inputMode="numeric"
                value={maxSitesStr}
                onChange={(e) => setMaxSitesStr(e.target.value)}
                placeholder="(blank = unlimited)"
                style={{
                  width: "100%", padding: "8px 10px",
                  border: `1px solid ${TOKENS.border}`,
                  borderRadius: TOKENS.radiusXs, fontSize: 14,
                }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 11.5, color: TOKENS.fg3, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
                Data retention (days)
              </Typography>
              <input
                type="text"
                inputMode="numeric"
                value={retentionStr}
                onChange={(e) => setRetentionStr(e.target.value)}
                placeholder="(blank = unlimited)"
                style={{
                  width: "100%", padding: "8px 10px",
                  border: `1px solid ${TOKENS.border}`,
                  borderRadius: TOKENS.radiusXs, fontSize: 14,
                }}
              />
            </Box>
          </Box>
        )}
        {err ? (
          <Typography sx={{ fontSize: 12, color: TOKENS.dangerInk, px: 2, pb: 1 }}>{err}</Typography>
        ) : null}
      </SectionCard>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// CertVendorsCard — surfaces org_cert_provider_config rows (enable +
// per-vendor cert quota + current usage) so a super-admin no longer
// needs SQL / curl to flip a vendor for an organisation.
//
// This is Option X of the SSL vendor licensing audit (direct admin
// endpoint, no SKU projection). Option Y — folding into Licensing v2
// via effective_features — ships in a separate PR.
//
// Backend contract (admin/server/routes/api/admin/ssl.js):
//   GET  /ssl/org/:orgId/cert_providers              → list rows
//   PUT  /ssl/org/:orgId/cert_providers/:provider    → upsert {enabled?, max_certs?}
//
// Vendors for which the org has no row are rendered with defaults
// (enabled=false, max_certs=0); the first PUT creates the row.
// ---------------------------------------------------------------------------
const VENDOR_CATALOG = [
  { id: "zerossl",  label: "ZeroSSL",            description: "Production default. Free 90-day DV certs renewed automatically." },
  { id: "acm",      label: "AWS ACM",            description: "AWS Certificate Manager for sites that terminate behind ACM-eligible AWS services." },
  { id: "digicert", label: "DigiCert DV",        description: "DigiCert OV/EV when an organisation needs a paid CA — counts against the quota." },
  { id: "custom",   label: "Custom PEM upload",  description: "Operator-uploaded PEM. No quota — the operator brings their own cert + key." },
  { id: "origin",   label: "Layerd Origin CA",   description: "Internal Layerd origin CA for proxy↔origin trust. Internal-only, no public chain." },
];

const MAX_CERTS_CAP = 1000;

function CertVendorsCard({ orgId, onSaved }) {
  const [rowsByProvider, setRowsByProvider] = useState({});
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [snack, setSnack] = useState(null); // { severity, msg }
  const [savingProvider, setSavingProvider] = useState(null);

  const showSnack = (severity, msg) => setSnack({ severity, msg });

  const fetchRows = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await adminAxios.get(`/ssl/org/${orgId}/cert_providers`);
      const payload = res?.data;
      // Accept either { providers: [...] } or a bare array — be defensive.
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.providers)
          ? payload.providers
          : Array.isArray(payload?.data)
            ? payload.data
            : [];
      const next = {};
      list.forEach((row) => {
        if (row && row.provider) next[row.provider] = row;
      });
      setRowsByProvider(next);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403 || e?.status === 403) {
        setForbidden(true);
      } else {
        const msg = e?.response?.data?.message || e?.message || "Failed to load cert vendors";
        showSnack("error", msg);
      }
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Vendor list = catalog order + any extra providers the CP returned
  // that aren't in the catalog (e.g. a future "letsencrypt" row).
  const vendorList = useMemo(() => {
    const catalogIds = new Set(VENDOR_CATALOG.map((v) => v.id));
    const extras = Object.keys(rowsByProvider)
      .filter((p) => !catalogIds.has(p))
      .sort()
      .map((id) => ({ id, label: id, description: null }));
    return [...VENDOR_CATALOG, ...extras];
  }, [rowsByProvider]);

  const putProvider = async (provider, body) => {
    setSavingProvider(provider);
    try {
      await adminAxios.put(`/ssl/org/${orgId}/cert_providers/${provider}`, body);
      showSnack("success", "Updated");
      await fetchRows();
      onSaved && onSaved();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Save failed";
      showSnack("error", msg);
      // Revert local state by refetching authoritative rows.
      await fetchRows();
    } finally {
      setSavingProvider(null);
    }
  };

  const handleToggle = (vendor, currentEnabled) => {
    const next = !currentEnabled;
    // Optimistic UI bump — flicker is shorter than the round-trip.
    setRowsByProvider((prev) => ({
      ...prev,
      [vendor.id]: { ...(prev[vendor.id] || {}), provider: vendor.id, enabled: next },
    }));
    putProvider(vendor.id, { enabled: next });
  };

  const handleQuotaBlur = (vendor, raw, originalValue) => {
    const trimmed = String(raw ?? "").trim();
    if (trimmed === "") {
      // Empty → revert visually; no PUT.
      setRowsByProvider((prev) => ({
        ...prev,
        [vendor.id]: { ...(prev[vendor.id] || {}), provider: vendor.id, max_certs: originalValue },
      }));
      return;
    }
    const n = Number(trimmed);
    if (!Number.isInteger(n) || n < 0 || n > MAX_CERTS_CAP) {
      showSnack("error", `Quota must be an integer between 0 and ${MAX_CERTS_CAP}.`);
      setRowsByProvider((prev) => ({
        ...prev,
        [vendor.id]: { ...(prev[vendor.id] || {}), provider: vendor.id, max_certs: originalValue },
      }));
      return;
    }
    if (n === originalValue) return; // no-op
    setRowsByProvider((prev) => ({
      ...prev,
      [vendor.id]: { ...(prev[vendor.id] || {}), provider: vendor.id, max_certs: n },
    }));
    putProvider(vendor.id, { max_certs: n });
  };

  // Body rendering
  let body;
  if (loading && Object.keys(rowsByProvider).length === 0) {
    body = (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  } else if (forbidden) {
    body = (
      <Typography sx={{ fontSize: 13, color: TOKENS.fg2, p: 1 }}>
        Cert-vendor management is not enabled for this organisation. Contact engineering.
      </Typography>
    );
  } else {
    body = (
      <Box>
        {vendorList.map((vendor, idx) => {
          const row = rowsByProvider[vendor.id] || {};
          const enabled = !!row.enabled;
          const maxCerts = Number.isInteger(row.max_certs) ? row.max_certs : 0;
          const used = Number.isInteger(row.used) ? row.used : 0;
          const isSaving = savingProvider === vendor.id;
          return (
            <Box
              key={vendor.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                py: 1.5,
                borderBottom: idx < vendorList.length - 1 ? `1px solid ${TOKENS.borderSubtle}` : "none",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: TOKENS.fg1 }}>
                  {vendor.label}
                </Typography>
                {vendor.description ? (
                  <Typography sx={{ fontSize: 12, color: TOKENS.fg3, mt: 0.25 }}>
                    {vendor.description}
                  </Typography>
                ) : null}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, flexShrink: 0 }}>
                <Switch
                  size="small"
                  checked={enabled}
                  disabled={isSaving}
                  onChange={() => handleToggle(vendor, enabled)}
                />
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                  <Typography sx={{ fontSize: 10.5, color: TOKENS.fg3, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.25 }}>
                    Quota (0 = no quota)
                  </Typography>
                  <input
                    type="number"
                    min={0}
                    max={MAX_CERTS_CAP}
                    defaultValue={maxCerts}
                    key={`${vendor.id}-${maxCerts}`}
                    disabled={isSaving}
                    onBlur={(e) => handleQuotaBlur(vendor, e.target.value, maxCerts)}
                    style={{
                      width: 88,
                      padding: "6px 8px",
                      border: `1px solid ${TOKENS.border}`,
                      borderRadius: TOKENS.radiusXs,
                      fontSize: 13,
                      background: isSaving ? TOKENS.bgSunken : "#fff",
                    }}
                  />
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: "10px",
                    py: "3px",
                    borderRadius: 999,
                    background: TOKENS.bgSunken,
                    color: TOKENS.fg2,
                    border: `1px solid ${TOKENS.border}`,
                    fontSize: 12,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  Used: {used}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <SectionCard
        title="Cert vendors"
        description="Enable or disable a CA per organisation and set a managed-cert quota. Quota = 0 means no quota (vendor is on but uncapped)."
      >
        {body}
      </SectionCard>
      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnack(null)}
          severity={snack?.severity || "info"}
          variant="filled"
          sx={{ fontSize: 13 }}
        >
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LicensingDetail;
